import { parseUserDoc, parseUserPrivateDoc } from '@db/Users/Common';
import { createNotFoundAppError, isPermissionDeniedError } from '@helpers/Errors';
import { uniqueBy } from '@helpers/Parsers';
import db, { getDocsIn } from '@helpers/firebase/Firestore';
import { firestore } from '@helpers/firebase/Initialize';
import { User } from '@types';

export async function getUser(userId: string) {
  try {
    const docSnap = await db.getDoc(db.doc(firestore, 'users', userId));
    if (docSnap.exists()) {
      return parseUserDoc(docSnap);
    }
  } catch (error) {
    // Firestore returns permission-denied for unreadable docs. Keep hidden and
    // missing users indistinguishable to callers.
    if (!isPermissionDeniedError(error)) throw error;
  }

  throw createMissingUserDocError(userId);
}

export async function getUserPrivate(userId: string) {
  try {
    const docSnap = await db.getDoc(db.doc(firestore, 'users', userId, 'private', userId));
    if (docSnap.exists()) {
      return parseUserPrivateDoc(docSnap);
    }
  } catch (error) {
    // Firestore returns permission-denied for unreadable docs. Keep hidden and
    // missing private user docs indistinguishable to callers.
    if (!isPermissionDeniedError(error)) throw error;
  }

  throw createMissingUserDocError(userId);
}

export async function getUsersIn(userIds: string[], constraints: db.QueryConstraint[] = []) {
  const docs = await getDocsIn('users', db.documentId(), 'in', userIds, constraints);
  return docs.map((doc) => parseUserDoc(doc));
}

export async function getUsersInDiff(desiredUserIds: Set<string>, haveUsers: User[]) {
  const haveUserIds = new Set(haveUsers.map((user) => user.userId));
  const needUserIds = [...desiredUserIds.values()].filter((userId) => !haveUserIds.has(userId));
  const users = await getUsersIn([...needUserIds.values()]);
  return [...users, ...haveUsers.filter((user) => desiredUserIds.has(user.userId))];
}

export async function getMostFollowedUsers() {
  const q = db.query(
    db.collection(firestore, 'users'),
    db.orderBy('numFollowers', 'desc'),
    db.limit(20)
  );
  const querySnapshot = await db.getDocs(q);
  return querySnapshot.docs.map((doc) => parseUserDoc(doc));
}

export async function getGroupMembers(
  groupId: string,
  constraints: db.QueryConstraint[] = []
): Promise<User[]> {
  const q = db.query(
    db.collection(firestore, 'users'),
    db.where('groupIds', 'array-contains', groupId),
    ...constraints
  );
  const snapshot = await db.getDocs(q);
  return snapshot.docs.map((doc) => parseUserDoc(doc));
}

export async function getGroupsMembers(
  groupIds: string[],
  constraints: db.QueryConstraint[] = []
): Promise<User[]> {
  const docs = await getDocsIn('users', 'groupIds', 'array-contains-any', groupIds, constraints);
  const users = docs.map((doc) => parseUserDoc(doc));
  // dedup data in case a user has groupIds across chunks
  return uniqueBy(users, 'userId');
}

function createMissingUserDocError(userId: string) {
  return createNotFoundAppError(new Error('User document does not exist. userId: ' + userId));
}
